import { Collection, Ref, RefBool, RefNumber, RefString } from "@tblabs/truffle";
import { Product } from "../../Models/Product/Product";
import { BasketItemType } from "../../Models/Basket/BasketItemType";
import { DatesRange } from "../../Models/Dates/DatesRange";
import { IBasketStorage } from "./Storage/IBasketStorage";
import { BasketStorageItem, RentBasketStorageItem } from "./Storage/BasketStorageItem";
import { RentBasketItem } from "./RentBasketItem";
import { SaleBasketItem } from "./SaleBasketItem";
import { BasketItem } from "./BasketItem";
import { BasketConfig } from "./BasketConfig";
import { Discounter } from "./Discounter";


export class Basket
{
    public Items = new Collection<BasketItem>();
    public TotalPriceBeforeDiscounts = new RefNumber();
    public TotalPriceAfterDiscounts = new RefNumber();
    public TotalDeposits = new RefNumber();
    public IsAnythingToRent = new RefBool();
    public DeliveryCost = new RefNumber();

    public get AnythingToRent(): boolean
    {
        return this.Items.Items.some(x => x.Type == BasketItemType.Rent);
    }

    public get RentTime(): DatesRange | undefined
    {
        return this.Items.Items.find(x => x instanceof RentBasketItem)?.Dates;
    }

    public get Config(): BasketConfig
    {
        return this._basketConfig;
    }

    public get Discounter(): Discounter
    {
        return this._discounter;
    }

    constructor(
        private _storage: IBasketStorage,
        private _products: Product[],
        public _basketConfig: BasketConfig,
        private _discounter: Discounter)
    { 
        this.Items.OnChange(items =>
        {
            // console.log("[Basket] items changed", items.length);
            this.Update();

            const raws: BasketStorageItem[] = items.map(x => x.AsRaw())
                .filter(x => x !== undefined);

            _storage.Update(raws);
        })

        const storedItems: BasketStorageItem[] = _storage.Get();

        try
        {
            if (storedItems.length > 0)
            {
                storedItems.forEach(raw =>
                {
                    const item = this.CreateItem(raw);

                    if (item)
                        this.Add(item)
                })
            }
        }
        catch (e)
        {
            console.error(e);
            _storage.Clear();
        }
    }

    private CreateItem(raw: BasketStorageItem): BasketItem | undefined
    {
        const product = this._products.find(x => x.Id == raw.ProductId);
        const productOptionId = new RefString(raw.ProductOptionId);
        const quantity = new RefNumber(raw.Quantity || 1);

        switch (raw.Type)
        {
            case BasketItemType.Rent:
                const rawRent = raw as RentBasketStorageItem;
                const dates = new DatesRange(rawRent.RentTime.Start, rawRent.RentTime.End);
                return new RentBasketItem(this, product, productOptionId, quantity, dates);
            case BasketItemType.Sale:
                return new SaleBasketItem(this, product, productOptionId, quantity);
            default:
                console.error("Invalid basket item type. Clearing basket.");
                this.Clear();
        }
    }

    public Add(item: BasketItem): boolean
    {
        if (this.IsAlreadyIn(item))
        {
            return false;
        }

        item.OnChange(() => this.Update());

        this.Items.Add(item);

        return true;
    }

    public UpdateItem(item: BasketItem): boolean
    {
        this.Remove(item);
        return this.Add(item);
    }

    public IsAlreadyIn(item: BasketItem): boolean
    {
        return this.Items.Items.find(x => x.Type == item.Type
            && x.Product?.Id == item.Product?.Id
            && x.ProductOptionId.value == item.ProductOptionId.value) !== undefined;
    }

    public GetItemBy(type: BasketItemType, productId: string, optionId?: string): BasketItem | undefined
    {
        if (!optionId)
            return this.Items.Items.find(x => x.Type == type && x.Product?.Id == productId);

        return this.Items.Items.find(x => x.Type == type && x.Product?.Id == productId && x.ProductOptionId.value == optionId);
    }

    public Remove(item: BasketItem): void
    {
        this.Items.Remove(item);
    }

    public GetDepositsSum(): number
    {
        return this.TotalDeposits.value;
    }

    public Clear(): void
    {
        this.Items.Clear();
        this.Update();
    }

    private Update(): void
    {
        this.IsAnythingToRent.value = this.Items.Items.some(x => x.Type == BasketItemType.Rent);
        this.TotalPriceBeforeDiscounts.value = this.Items.Items.reduce((prev, cur) => prev + cur.FinalPriceBeforeDiscount.value, 0);
        this.TotalPriceAfterDiscounts.value = this.Items.Items.reduce((prev, cur) => prev + cur.FinalPriceAfterDiscount.value, 0);
        this.TotalDeposits.value = this.Items.Items
            .filter(x => x instanceof RentBasketItem)
            .reduce((prev, cur) => prev + cur.FinalDeposit.value, 0);
        this.DeliveryCost.value = this.TotalPriceAfterDiscounts.value >= 299
            ? this.Config.DeliveryCostForPriceAbove300.value
            : this.Config.DeliveryCostForPriceBelow300.value;

        const rawBasketItems = this.Items.Items
            .map(x => x.AsRaw()).filter(x => x !== undefined) as BasketStorageItem[];
            
        this._storage.Update(rawBasketItems);
    }

    public get IsEmpty(): boolean
    {
        return this.Items.Count == 0;
    }

    public get WhenEmpty(): Ref<boolean>
    {
        const ref = new RefBool(this.IsEmpty);

        this.Items.OnChange(() => ref.value = this.IsEmpty);
        
        return ref;
    }
}
